import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  type ComponentRef,
  HostBinding,
  inject,
} from '@angular/core';
import type { UntypedFormGroup } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ActionButtonModule } from '@clean-code/shared/components/ui-action-button';
import {
  type IDashboardWidgetConfigDto,
  type IWidgetTypeConfigComponent,
  WidgetTypeProvider,
} from '@clean-code/shared/components/ui-dashboard';
import { DashboardFacade } from '@clean-code/shared/dashboard/dashboard/domain';
import { DashboardWidgetFacade } from '@clean-code/shared/dashboard/widget/domain';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { TranslocoModule } from '@jsverse/transloco';
import { DynamicModule } from 'ng-dynamic-component';
import { BehaviorSubject, Subject } from 'rxjs';

@Component({
  templateUrl: './widget-type-dialog-config.component.html',
  styleUrls: ['./widget-type-dialog-config.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDialogModule,
    TranslocoModule,
    DynamicModule,
    FontAwesomeModule,
    ActionButtonModule,
  ],
})
export class WidgetTypeConfigDialogComponent {
  @HostBinding('class') class = 'widget-type-dialog-config-component';

  public dialogRef = inject(MatDialogRef<WidgetTypeConfigDialogComponent>);

  faTimes = faTimes;
  public form!: UntypedFormGroup;
  // public component: { new (): IWidgetTypeConfigComponent };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public inputs$ = new Subject<any>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public outputs: any;
  public component$ = new BehaviorSubject<{
    new (): IWidgetTypeConfigComponent;
  }>(null);

  private widgetFacade = inject(DashboardWidgetFacade);
  private dashboardFacade = inject(DashboardFacade);
  public widget = this.widgetFacade.widget;

  constructor() {
    const widgetTypeProvider = inject(WidgetTypeProvider);
    const provider = widgetTypeProvider.getProvider(
      this.widget().type as string,
    );
    if (provider) {
      this.component$.next(provider.configCmp);
    }
  }

  public close() {
    this.dialogRef.close();
  }

  public componentCreated(compRef: ComponentRef<IWidgetTypeConfigComponent>) {
    this.form = compRef.instance.form;
  }

  public save(): void {
    const widget = this.widget();
    const widgetConfig = {
      id: widget.id,
      dashboardId: widget.dashboardId,
      type: widget.type,
      config: this.form.value,
    } as IDashboardWidgetConfigDto;

    if (!widgetConfig) {
      this.dialogRef.close();
      return;
    }

    this.widgetFacade
      .updateConfigWithResult$(widgetConfig)
      .subscribe({
        next: (updated) => {
          this.dashboardFacade.updateWidget(updated);
          this.dialogRef.close();
        },
        error: () => this.dialogRef.close(),
      });
  }
}
